import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform, useMotionValueEvent, useReducedMotion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { MANIFESTO, ASSETS } from "@/content/site";
import Reveal from "@/components/Reveal";
import ContextLine from "@/components/ContextLine";
import useScrollProgress from "@/hooks/useScrollProgress";
import useMediaQuery from "@/hooks/useMediaQuery";

const CHAPTERS = [
  { n: "٠١", title: MANIFESTO.chapters[0].title, text: MANIFESTO.chapters[0].short, full: MANIFESTO.chapters[0].body },
  { n: "٠٢", title: MANIFESTO.chapters[1].title, text: MANIFESTO.chapters[1].short, full: MANIFESTO.chapters[1].body },
  { n: "٠٣", title: "ثلاث ركائز نقود بها الأثر", pillars: MANIFESTO.pillars },
];
const TRANSITION_LINE = "من فهم السياق… إلى أثرٍ ملموس";

/* Climbing Siaq line with three nodes on the staircase */
function ClimbLine({ active }) {
  const nodes = [{ t: "76%" }, { t: "50%" }, { t: "22%" }];
  return (
    <div className="absolute inset-y-0 pointer-events-none z-10" style={{ left: "5%", width: "30%" }} aria-hidden="true">
      <ContextLine d="M40,760 C10,560 120,470 90,360 C60,250 160,170 150,20" viewBox="0 0 200 760" vertical className="absolute inset-0 w-full h-full" strokeWidth={2.5} />
      {nodes.map((n, i) => (
        <span key={i} className="absolute rounded-full transition-all duration-500" style={{
          top: n.t, left: `${34 + i * 6}%`,
          width: active >= i ? 15 : 10, height: active >= i ? 15 : 10,
          backgroundColor: active >= i ? "var(--coral)" : "rgba(234,173,140,0.6)",
          boxShadow: active === i ? "0 0 0 5px rgba(229,107,111,0.25)" : "none",
        }} />
      ))}
    </div>
  );
}

function ChapterCopy({ chapter, isHome }) {
  if (chapter.pillars) {
    return (
      <div>
        <div className="flex items-baseline gap-4">
          <span className="font-display text-4xl sm:text-5xl" style={{ color: "var(--peach)" }}>٠٣</span>
          <h3 className="font-display text-white text-2xl sm:text-3xl leading-snug">{chapter.title}</h3>
        </div>
        <ul className="mt-6 space-y-4">
          {chapter.pillars.map((p, i) => (
            <li key={p.title} className="flex items-center gap-3" style={{ marginInlineStart: `${i * 22}px` }}>
              <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: "var(--coral)" }} aria-hidden="true" />
              <span className="font-display text-white text-xl sm:text-2xl">{p.title}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }
  return (
    <div>
      <div className="flex items-baseline gap-4">
        <span className="font-display text-4xl sm:text-5xl" style={{ color: "var(--peach)" }}>{chapter.n}</span>
        <h3 className="font-display text-white text-2xl sm:text-3xl leading-snug">{chapter.title}</h3>
      </div>
      <p className="mt-5 leading-relaxed text-lg max-w-md" style={{ color: "rgba(255,255,255,0.86)" }}>{isHome ? chapter.text : chapter.full}</p>
    </div>
  );
}

function DesktopScene({ isHome }) {
  const trackRef = useRef(null);
  const [chapter, setChapter] = useState(0);
  const { scrollYProgress } = useScroll({ target: trackRef, offset: ["start start", "end end"] });
  const veilHeight = useTransform(scrollYProgress, [0.88, 1], ["0%", "100%"]);
  const phraseOpacity = useTransform(scrollYProgress, [0.9, 0.98], [0, 1]);
  useMotionValueEvent(scrollYProgress, "change", (v) => {
    setChapter(v < 0.3 ? 0 : v < 0.62 ? 1 : 2);
  });

  return (
    <div ref={trackRef} className="relative" style={{ minHeight: "240svh", backgroundColor: "var(--navy)" }}>
      <div className="sticky" style={{ top: "var(--header-height)", height: "calc(100svh - var(--header-height))" }}>
        {/* visual-frame — the ONLY element that clips */}
        <div className="absolute inset-0 overflow-hidden">
          <img src={ASSETS.staircase} alt="سلّم صاعد نحو الضوء داخل عمارة حديثة يرمز إلى الصعود نحو الأثر" width="1280" height="720" loading="lazy" className="absolute inset-0 w-full h-full object-cover object-[70%_center]" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(270deg, rgba(28,42,58,0.96) 0%, rgba(28,42,58,0.82) 30%, rgba(28,42,58,0.28) 48%, rgba(28,42,58,0) 60%)" }} />
          <ClimbLine active={chapter} />

          {/* Copy on the dark wall (left) */}
          <div className="relative h-full mx-auto max-w-[1400px] px-6 sm:px-10">
            <div dir="rtl" className="absolute z-20 text-right top-1/2 -translate-y-1/2" style={{ right: "clamp(32px, 7vw, 104px)", left: "auto", width: "min(440px, 40vw)" }}>
              <span className="eyebrow mb-6">عن سياق</span>
              <AnimatePresence mode="wait">
                <motion.div key={chapter} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}>
                  <ChapterCopy chapter={CHAPTERS[chapter]} isHome={isHome} />
                </motion.div>
              </AnimatePresence>
              {chapter === 2 && isHome && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
                  <Link to="/about" data-testid="about-cta" className="group mt-8 inline-flex items-center gap-2 text-white border border-white/30 px-7 py-4 text-base font-medium hover:bg-white hover:text-navy transition-colors">
                    تعرّف على سياق <ArrowLeft size={18} className="transition-transform group-hover:-translate-x-1" />
                  </Link>
                </motion.div>
              )}
            </div>
          </div>

          {/* Transition to أثرنا — navy rises + phrase + horizontal line (scroll-tied) */}
          <motion.div className="absolute inset-x-0 bottom-0 pointer-events-none" style={{ height: veilHeight, background: "linear-gradient(to top, var(--navy) 30%, rgba(40,62,85,0.6) 70%, transparent)" }} aria-hidden="true" />
          <motion.div className="absolute inset-x-0 bottom-10 flex flex-col items-center px-6" style={{ opacity: phraseOpacity }} data-testid="about-transition">
            <p className="font-display text-white text-xl sm:text-2xl text-center">{TRANSITION_LINE}</p>
            <ContextLine d="M0,20 C300,60 900,-20 1200,20" viewBox="0 0 1200 40" className="mt-4 h-8 w-64 sm:w-96" strokeWidth={2} />
          </motion.div>
        </div>
      </div>
    </div>
  );
}

function MobileScene({ isHome }) {
  const trackRef = useRef(null);
  const p = useScrollProgress(trackRef);
  const ch = p < 0.34 ? 0 : p < 0.67 ? 1 : 2;
  const phraseOpacity = p > 0.9 ? Math.min(1, (p - 0.9) / 0.09) : 0;

  return (
    <div ref={trackRef} className="relative" style={{ minHeight: "300svh", backgroundColor: "var(--navy)" }}>
      <div className="sticky overflow-hidden" style={{ top: "var(--header-height)", height: "calc(100svh - var(--header-height))" }}>
        {/* Single staircase image (shown ONCE) */}
        <img src={ASSETS.staircase} alt="سلّم صاعد نحو الضوء داخل عمارة حديثة يرمز إلى الصعود نحو الأثر" width="1280" height="720" loading="lazy" className="absolute inset-0 w-full h-full object-cover object-[72%_center]" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(28,42,58,0.97) 20%, rgba(28,42,58,0.55) 52%, rgba(28,42,58,0.12) 80%)" }} />
        <ClimbLine active={ch} />

        <div dir="rtl" className="absolute inset-x-0 bottom-0 p-6 pb-10 text-right">
          <span className="eyebrow mb-4">عن سياق</span>
          <AnimatePresence mode="wait">
            <motion.div key={ch} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}>
              <ChapterCopy chapter={CHAPTERS[ch]} isHome={isHome} />
            </motion.div>
          </AnimatePresence>
          {ch === 2 && isHome && (
            <Link to="/about" data-testid="about-cta-mobile" className="mt-6 inline-flex items-center gap-2 text-white border border-white/30 px-6 py-3.5 font-medium">
              تعرّف على سياق <ArrowLeft size={18} />
            </Link>
          )}
          {isHome && (
            <p className="mt-6 font-display text-white text-lg" style={{ opacity: phraseOpacity }} data-testid="about-transition">
              {TRANSITION_LINE}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/* Static fallback (reduced-motion): image once, then chapters stacked as text */
function MobileStatic({ isHome }) {
  return (
    <div style={{ backgroundColor: "var(--navy)" }}>
      <div className="relative overflow-hidden" style={{ height: "56svh" }}>
        <img src={ASSETS.staircase} alt="سلّم صاعد نحو الضوء داخل عمارة حديثة يرمز إلى الصعود نحو الأثر" width="1280" height="720" loading="lazy" className="absolute inset-0 w-full h-full object-cover object-[72%_center]" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(28,42,58,0.95) 12%, rgba(28,42,58,0.3) 60%, transparent)" }} />
        <div dir="rtl" className="absolute bottom-0 inset-x-0 p-6"><span className="eyebrow">عن سياق</span></div>
      </div>
      <div dir="rtl" className="px-6 py-12 space-y-12 text-right">
        {CHAPTERS.map((c) => (
          <div key={c.n}><ChapterCopy chapter={c} isHome={isHome} /></div>
        ))}
        {isHome && (
          <>
            <Link to="/about" data-testid="about-cta-mobile" className="inline-flex items-center gap-2 text-white border border-white/30 px-6 py-3.5 font-medium">
              تعرّف على سياق <ArrowLeft size={18} />
            </Link>
            <p className="font-display text-white text-lg" data-testid="about-transition">{TRANSITION_LINE}</p>
          </>
        )}
      </div>
    </div>
  );
}

export default function AboutManifesto({ full = false }) {
  const reduced = useReducedMotion();
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const isHome = !full;

  let scene;
  if (reduced) scene = <MobileStatic isHome={isHome} />;
  else if (isDesktop) scene = <DesktopScene isHome={isHome} />;
  else scene = <MobileScene isHome={isHome} />;

  return (
    <section data-testid="about-manifesto-section" style={{ backgroundColor: "var(--navy)" }}>
      {scene}

      {full && (
        <div className="bg-paper py-24 sm:py-32">
          <div className="mx-auto max-w-[1400px] px-6 sm:px-10">
            <Reveal>
              <div className="grid sm:grid-cols-2 gap-10">
                <div>
                  <h3 className="font-display text-navy text-2xl">{MANIFESTO.vision.title}</h3>
                  <p className="mt-3 text-blue leading-relaxed text-lg">{MANIFESTO.vision.body}</p>
                </div>
                <div>
                  <h3 className="font-display text-navy text-2xl">{MANIFESTO.mission.title}</h3>
                  <p className="mt-3 text-blue leading-relaxed text-lg">{MANIFESTO.mission.body}</p>
                </div>
              </div>
            </Reveal>
            <Reveal>
              <h3 className="font-display text-navy text-2xl mt-16 mb-6">قيمنا</h3>
              <div className="flex flex-wrap gap-3">
                {MANIFESTO.values.map((value) => (
                  <details key={value.title} className="group border border-navy/15 bg-white px-5 py-3 text-navy sm:max-w-sm">
                    <summary className="cursor-pointer font-medium list-none flex items-center justify-between gap-5">
                      {value.title}<span aria-hidden="true" className="text-coral group-open:rotate-45 transition-transform">＋</span>
                    </summary>
                    <p className="mt-3 text-blue text-sm leading-relaxed">{value.text}</p>
                  </details>
                ))}
              </div>
            </Reveal>
            <Reveal>
              <h3 className="font-display text-navy text-2xl mt-16 mb-6">ركائزنا الاستراتيجية</h3>
              <div className="grid sm:grid-cols-3 gap-8">
                {MANIFESTO.pillars.map((p) => (
                  <div key={p.title} className="border-t-2 pt-4" style={{ borderColor: "var(--coral)" }}>
                    <h4 className="font-display text-navy text-lg">{p.title}</h4>
                    <p className="mt-2 text-blue text-sm leading-relaxed">{p.text}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      )}
    </section>
  );
}
